import React, { useState, useEffect, useRef } from 'react';
import { useFetcher, useLocation, useNavigate } from '@remix-run/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import TickerSearchModal from './TickerSearchModal';
import { captureException } from "~/utilities/sentry.utility";

const DesktopHeaderSearch: React.FC = () => {
    const fetcher = useFetcher();
    const [suggestions, setSuggestions] = useState<Array<{
        ticker_symbol: string;
        name: string;
        market_cap: number;
        logo_url: string;
        ticker_id: string;
        exchange: string;
        company_id: string;
    }>>([]);
    const inputRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const navigate = useNavigate();
    const [isFocused, setIsFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
        ticker_id: string;
        exchange: string;
        ticker_symbol: string;
        company_id: string;
    } | null>(null);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev > 0 ? prev - 1 : -1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0) {
                    handleSelectStock(suggestions[activeIndex]);
                }
                break;
            case 'Escape':
                setSuggestions([]);
                setActiveIndex(-1);
                break;
        }
    };

    const handleSelectStock = (stock: { ticker_id: string; exchange: string; ticker_symbol: string; company_id: string; }) => {
        console.log(stock);

        // Check if all required properties are present
        if (!stock.ticker_id || !stock.exchange || !stock.ticker_symbol || !stock.company_id) {
            console.log('Waiting for complete stock data...', stock);
            // Store the pending navigation and wait for data to be complete
            setPendingNavigation(stock);
            return;
        }

        // Clear any pending navigation
        setPendingNavigation(null);

        setSuggestions([]);
        setActiveIndex(-1);
        setShowSearchModal(false);

        // clear the input
        if (inputRef.current) {
            (inputRef.current as HTMLInputElement).value = '';
        }

        navigate(`/ticker/${stock.ticker_id}/${stock.exchange}/${stock.ticker_symbol}?type=company&cid=${stock.company_id}`, { replace: false, state: { needRefresh: true } });
    };

    const handleSearch = debounce(async (value: string) => {
        if (!value) {
            setSuggestions([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        fetcher.load(`/api/tickers?search_term=${encodeURIComponent(value)}`);
    }, 300);

    useEffect(() => {
        if (fetcher.data) {
            setIsLoading(false);
            if (Array.isArray(fetcher.data)) {
                setSuggestions(fetcher.data.slice(0, 5));

                // Check if we have a pending navigation and if the data is now complete
                if (pendingNavigation) {
                    const completeStock = fetcher.data.find(stock =>
                        stock.ticker_symbol === pendingNavigation.ticker_symbol &&
                        stock.ticker_id &&
                        stock.exchange &&
                        stock.ticker_symbol &&
                        stock.company_id
                    );

                    if (completeStock) {
                        console.log('Found complete stock data, proceeding with navigation:', completeStock);
                        setPendingNavigation(null);
                        setSuggestions([]);
                        setActiveIndex(-1);
                        setShowSearchModal(false);

                        // clear the input
                        if (inputRef.current) {
                            (inputRef.current as HTMLInputElement).value = '';
                        }

                        navigate(`/ticker/${completeStock.ticker_id}/${completeStock.exchange}/${completeStock.ticker_symbol}?type=company&cid=${completeStock.company_id}`, { replace: false, state: { needRefresh: true } });
                    }
                }
            } else if (fetcher.data && typeof fetcher.data === 'object' && 'error' in fetcher.data) {
                console.error("Search error:", (fetcher.data as { error: string }).error);
                captureException((fetcher.data as { error: string }).error);
                setSuggestions([]);
            }
        }
    }, [fetcher.data, pendingNavigation, navigate]);

    const [showSearchModal, setShowSearchModal] = useState(false);
    const [currentHash, setCurrentHash] = useState('');
    const location = useLocation();

    useEffect(() => {
        setCurrentHash(location.hash);

        if (location.hash) {
            const element = document.querySelector(location.hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location.hash]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    return (
        <header id="desktop-header" className="hidden lg:block inset-x-0 top-0 z-20 sticky bg-white">
            <nav aria-label="Global" className="flex justify-between px-8 py-2.5">
                <div className="flex flex-1 justify-end">
                    <div className="relative w-[300px]">
                        <input
                            ref={inputRef}
                            type="text"
                            id="search-bar-modal-text"
                            className="w-full px-4 py-2 text-gray-900 border border-gray-300 rounded-full focus:ring-2 focus:ring-green-900 focus:border-transparent pr-10 bg-background-dark placeholder:text-sm"
                            placeholder="Search Tickers..."
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                setIsFocused(true);
                                // Clear any existing blur timeout
                                if (blurTimeoutRef.current) {
                                    clearTimeout(blurTimeoutRef.current);
                                    blurTimeoutRef.current = null;
                                }
                            }}
                            onBlur={() => {
                                // Set a timeout to hide suggestions after blur
                                blurTimeoutRef.current = setTimeout(() => {
                                    setIsFocused(false);
                                    setActiveIndex(-1);
                                }, 300); // Increased from 200ms to 300ms to give more time for clicks
                            }}
                            onChange={(e) => {
                                const value = e.target.value.toLowerCase();
                                handleSearch(value);
                                setActiveIndex(-1);
                            }}
                            autoComplete='off'
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-600 size-4" />
                        </div>
                        {suggestions.length > 0 && isFocused && (
                            <div className="absolute w-full mt-1 bg-white border border-[#eaeff4] rounded-lg shadow-lg overflow-y-auto z-10">
                                {suggestions.map((stock: { ticker_symbol: string; name: string; market_cap: number; logo_url: string; ticker_id: string; exchange: string; company_id: string; }, index: number) => (
                                    <button
                                        key={stock.ticker_symbol}
                                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${index === activeIndex ? 'bg-green-100' : ''}`}
                                        onClick={() => handleSelectStock(stock)}
                                        role="option"
                                        aria-selected={index === activeIndex}
                                    >
                                        <div className="flex items-center">
                                            {stock.logo_url && (
                                                <img
                                                    src={stock.logo_url}
                                                    alt={stock.name}
                                                    className="w-6 h-6 mr-2"
                                                />
                                            )}
                                            <div>
                                                <div className="font-medium text-gray-900 text-xs">{stock.ticker_symbol}</div>
                                                <div className="text-gray-600">
                                                    <span className="font-bold text-gray-900 text-xs">{stock.name}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                        {isLoading && isFocused && (
                            <div className="absolute w-full mt-1 bg-white border border-[#eaeff4] rounded-lg shadow-lg p-2 text-center">
                                <span className="text-sm text-gray-500">Loading...</span>
                            </div>
                        )}
                    </div>
                </div>
            </nav>
            {
                showSearchModal && (
                    <TickerSearchModal showSearchModal={showSearchModal} setShowSearchModal={setShowSearchModal} mode={undefined} />
                )
            }
        </header>
    );
};

function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

export default DesktopHeaderSearch;
